import {CustomOrbitControls} from "./orbit-controls";
import {Viewer} from '../main';
import { CameraAlignerAdv, CameraAlignerBasic, LocalAligner } from '../data-aligner';
import { ChangeDetector, isMobile, mergeBoundingBoxes } from '../utilities';
import { SceneCoordinate, SphericalPosition } from '../projections';
import {
  PerspectiveCamera,
  Scene,
  Vector3,
} from 'three';
import { PointMarkupList } from "../markup";
import { MouseControls, touchToMouse } from ".";
import Hammer from "hammerjs";
import { onDoubleTap, onLongPress } from "./mobile-controls";
import { ContextData, showContextMenu } from "../../../context-menu";
import { PointProfile } from "../point-profile";

export interface OrbitPosition {
  orbit: Vector3,
  pivot: Vector3
  smooth: boolean;
}

export class PanoControls {
  public viewer: Viewer;
  public object: PerspectiveCamera;
  public domElement: HTMLCanvasElement;
  public mouseControls: MouseControls;
  public cameraAlignerAdv: CameraAlignerAdv;
  public cameraAlignerBasic: CameraAlignerBasic;
  public localAligner: LocalAligner;
  public pointMarkup: PointMarkupList;
  public pointProfile: PointProfile;
  private hammer: Hammer;
  private enabled = true;
  public direction = new Vector3(0, 0, 0);
  private up = new Vector3(0, 0, 1);
  public orbit: OrbitController;
  public flyControls: FlyMoveControls;

  constructor(viewer: Viewer, object: PerspectiveCamera, domElement: HTMLCanvasElement, props) {
    this.viewer = viewer;
    this.object = object;
    this.domElement = domElement;

    this.hammer = new Hammer(this.domElement);
    this.orbit = new OrbitController(this.viewer, this, this.up);
    this.flyControls = new FlyMoveControls(this);

    this.mouseControls = new MouseControls(this.viewer, this.object, 10, 90);
    this.pointMarkup = new PointMarkupList(this, props);
    this.pointProfile = new PointProfile(this, props);
    this.cameraAlignerAdv = new CameraAlignerAdv(this, props);
    this.cameraAlignerBasic = new CameraAlignerBasic(this);
    this.localAligner = new LocalAligner(this, props);

    this.initEvents();
  }

  get anyToolEnabled() {
    return this.pointMarkup.enabled
      || this.sceneLabels.enabled
      || this.measurements.enabled
      || this.cameraAlignerAdv.enabled
      || this.cameraAlignerBasic.enabled
      || this.localAligner.enabled;
  }

  get toolScenes(): Scene[] {
    return [
      this.pointProfile.scene,
      this.cameraAlignerAdv.scene,
      this.pointMarkup.scene,
      this.localAligner.scene
    ];
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get sceneLabels() {
    return this.viewer.imageLabels.sceneLabels;
  }

  get cadHandler() {
    return this.viewer.cadHandler;
  }

  get markers() {
    return this.viewer.markers;
  }

  get compass() {
    return this.viewer.compass;
  }

  get sceneTags() {
    return this.viewer.tags.sceneViewController;
  }

  get measurements() {
    return this.viewer.measurements;
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get picker() {
    return this.viewer.gpuPickers.default;
  }

  get fov(): number {
    return this.object.fov;
  }

  get angles(): number[] {
    return this.mouseControls.angles;
  }

  set angles(values) {
    this.mouseControls.angles = values;
  }

  get lookat() {
    return this.mouseControls.lookat
      .add(this.viewer.camera.position);
  }

  destroy() {
    this.pointProfile.destroy();
    this.removeEvents();
  }

  initEvents() {
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseWheel = this.onMouseWheel.bind(this);
    this.onMouseDoubleClick = this.onMouseDoubleClick.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.touchHandler = this.touchHandler.bind(this);

    this.domElement.addEventListener('mousedown', this.onMouseDown, false);
    this.domElement.addEventListener('mouseup', this.onMouseUp, false);
    this.domElement.addEventListener('wheel', this.onMouseWheel, false);
    this.domElement.addEventListener('dblclick', this.onMouseDoubleClick, false);
    this.domElement.addEventListener('mousemove', this.onMouseMove, false);
    this.domElement.addEventListener('touchstart', this.touchHandler, true);
    this.domElement.addEventListener('touchmove', this.touchHandler, true);
    this.domElement.addEventListener('touchend', this.touchHandler, true);
    this.domElement.addEventListener('touchcancel', this.touchHandler, true);

    if (!isMobile) return;

    // Doubletap and longpress are handled by hammer.js
    this.hammer.on('doubletap', event => onDoubleTap(this.domElement, event));
    this.hammer.on('press', event => onLongPress(this.domElement, event));
  }

  removeEvents() {
    this.domElement.removeEventListener('mousedown', this.onMouseDown, false);
    this.domElement.removeEventListener('mouseup', this.onMouseUp, false);
    this.domElement.removeEventListener('wheel', this.onMouseWheel, false);
    this.domElement.removeEventListener('dblclick', this.onMouseDoubleClick, false);
    this.domElement.removeEventListener('mousemove', this.onMouseMove, false);
    this.domElement.removeEventListener('touchstart', this.touchHandler, true);
    this.domElement.removeEventListener('touchmove', this.touchHandler, true);
    this.domElement.removeEventListener('touchend', this.touchHandler, true);
    this.domElement.removeEventListener('touchcancel', this.touchHandler, true);
    this.hammer.destroy();
  }

  resetSceneView() {
    this.mouseControls.angles = [0, 0];
    this.mouseControls.fov = 90;
  }

  setControlType(type) {
    this.orbit.setControls(type);
  }

  updateFarPlane(far) {
    this.object.far = far;
    this.orbit.camera.far = far;
  }

  getCameraTarget() {
    let cameraPosition = this.orbit.state
      ? this.orbit.activeTarget
      : this.object.position;

    return cameraPosition;
  }

  setOrbitValues(newOrbitState, position: OrbitPosition = null) {
    this.viewer.updatePickers();

    const stateChanged = this.orbit.state !== newOrbitState;
    if (!position && !stateChanged) return;

    let newOrbit;
    let newPivot;
    let smoothTransition = false;

    this.enabled = false;
    if (newOrbitState) {
      // Set our orbit controls position and target
      if (!position) {
        newOrbit = this.object.position;
        newPivot = new Vector3(0, 0, -1 * this.orbit.rotateLength)
          .applyQuaternion(this.object.quaternion)
          .add(newOrbit);
      } else {
        newOrbit = position.orbit;
        newPivot = position.pivot;
        smoothTransition = position.smooth;
      }

      this.orbit.setWalkMode(false);
      this.orbit.setPositionValues(newOrbit, newPivot,
        newOrbitState, smoothTransition);

      this.viewer.camera = this.orbit.camera;

      if (!smoothTransition) {
        this.flyControls.disable();
      }
    } else {
      this.orbit.controller.enabled = newOrbitState;
      this.viewer.camera = this.object;
    }

    this.enabled = true;

    // Set panoramic visibility
    if (this.cameraList) {
      this.cameraList.setMeshVisible(!newOrbitState);
    }

    this.viewer.handleWindowResize();
    this.viewer.updateCamera();
  }

  getPickerResults(event) {
    this.picker.update();
    const index = this.picker.value(event);
    return this.picker.getClosestCoordinate(index, event);
  }

  resetCamera(side) {
    let boundingBoxes = [
      this.pointclouds.combinedBoundingBox,
      this.sceneTags.combinedBoundingBox,
      this.cadHandler.combinedBoundingBox
    ];

    let boundingBox = null;
    boundingBoxes.forEach(bbox => {
      boundingBox = mergeBoundingBoxes(boundingBox, bbox);
    });

    if (!boundingBox) {
      return;
    }

    let newPosition = this.orbitFromBounds(
      boundingBox, side, true);

    this.setOrbitValues(true, newPosition);
  }

  orbitFromBounds(bbox: {min: Vector3, max: Vector3}, side, smooth): OrbitPosition {
    let boxSize = new Vector3()
      .add(bbox.max)
      .sub(bbox.min);

    let boxMean = new Vector3()
      .add(boxSize)
      .divideScalar(2.0)
      .add(bbox.min);

    boxSize.multiplyScalar(1.5);

    let boundSize = Math.max(boxSize.x, boxSize.y, boxSize.z);
    let height = bbox.min.z + boxSize.z*0.25;
    let pivot = new Vector3(boxMean.x, boxMean.y, height);

    // Make sure our initial height doesnt exceed the far plane
    let maxHeight = this.viewer.camera.far * 0.75;
    let radius = Math.min(maxHeight, boundSize / 2.0);

    let orbit = this.orbitWithAngle(pivot, radius, side);

    return {orbit, pivot, smooth};
  }

  orbitWithAngle(pivot, radius, side) {
    let theta;
    let phi;

    side = side.toLowerCase();
    switch (side) {
    case "top":
      theta = 270;
      phi = 0.01;
      break;
    case "bottom":
      theta = 270;
      phi = 179.99;
      break;
    case "right":
      theta = 0.0;
      phi = 90;
      break;
    case "left":
      theta = 180;
      phi = 90;
      break;
    case "front":
      theta = 270;
      phi = 90;
      break;
    case "back":
      theta = 90;
      phi = 90;
      break;
    default:
      theta = 0.0;
      phi = 0.0;
    }

    let orbit = new SphericalPosition()
      .fromValues(radius, theta, phi)
      .toVector3()
      .add(pivot);

    return orbit;
  }

  toggleCamera() {
    let checked = this.orbit.state;
    this.setOrbitValues(!checked);
  }

  onMouseMove(initialEvent) {
    initialEvent.preventDefault();

    const event = this.mouseControls.onMouseMove(initialEvent);

    if (event.mouseDown || event.scrolling) {
      this.viewer.clearCoordinates();
    } else {
      this.viewer.updateCoordinates(event);
    }

    if (this.measurements.enabled) {
      const measureDragging = this.measurements.onMouseMoveScene(event);
      if (measureDragging) return;
    } else if (this.pointMarkup.enabled) {
      const markupDragging = this.pointMarkup.onMouseMove(event);
      if (markupDragging) return;
    } else if (this.sceneLabels.enabled) {
      const labelDragging = this.sceneLabels.onMouseMove(event);
      if (labelDragging) return;
    } else if (this.cameraAlignerAdv.enabled) {
      this.cameraAlignerAdv.onMouseMove();
    } else if (this.pointProfile.drawing) {
      this.pointProfile.onMouseMove(event);
    } else if (this.localAligner.enabled) {
      this.localAligner.onMouseMoveScene();
    } else {
      this.sceneTags.onMouseMove(event);
    }

    this.compass.onMouseMove(event);
    this.markers.onMouseMove(event);

    if (!this.enabled || this.orbit.state || !this.mouseControls.movement) {
      return;
    }

    this.mouseControls.updateRotation(
      this.object.fov,
      this.domElement.clientWidth,
      this.domElement.clientHeight
    );
  }

  onKeyDown(event) {
    this.pointMarkup.onKeyDown(event);
    this.cameraAlignerBasic.onKeyDown(event);
  }

  onKeyUp(event) {
    this.pointMarkup.onKeyUp();
  }

  onMouseDown(event) {
    event.preventDefault();
    this.mouseControls.onMouseDown(event);
  }

  onMouseUp(initialEvent) {
    const event = this.mouseControls.onMouseUp(initialEvent);

    this.measurements.measureGrabFinished();
    this.orbit.toggle(true);

    if (!this.mouseControls.upEnabled || !this.enabled) {
      return;
    }

    // Check onMouseUp of all scene elements. Marker and compass events
    // must come first, but the order of everything else does not matter.
    const mouseClicked = this.markers.onMouseUp(event)
      || this.compass.onMouseUp(event)
      || this.pointMarkup.onMouseUp(event)
      || this.measurements.onMouseUp(event)
      || this.cameraAlignerAdv.onMouseUp(event)
      || this.localAligner.onMouseUp(event)
      || this.sceneLabels.onMouseUp(event);

    if (mouseClicked || event.mouseMoved) {
      return this.afterMouseUp();
    }

    if (event.isRightClick) {
      this.openContextMenu(event);
    }

    this.afterMouseUp();
  }

  onMouseWheel(event) {
    this.viewer.updatePickers();
    this.mouseControls.onMouseScroll();

    if (!this.enabled || this.orbit.state) return;

    event.preventDefault();
    this.mouseControls.onMouseWheel(event);
  }

  onMouseDoubleClick(event) {
    if ((!this.enabled) || (this.measurements.enabled)) return;
    if (!this.orbit.state) return;

    let {coordinate} = this.getPickerResults(event);
    if (!coordinate) return;

    const currentOrbit = this.orbit.activePosition;

    const vector = new Vector3()
      .add(coordinate)
      .sub(currentOrbit)
      .setLength(2.0);

    const orbit = new Vector3()
      .add(coordinate)
      .sub(vector);

    // Move to new pointcloud location
    this.flyControls.start(coordinate, orbit);
  }

  async openContextMenu(event) {
    if (this.anyToolEnabled) {
      return showContextMenu(event, false);
    }

    // Clicked scene coordinate
    const position = this.getPickerResults(event).coordinate;
    const sceneCoord = position
      ? new SceneCoordinate(position).toDataProjection().toArray()
      : null;

    // Tag attributes
    const selectedTags = this.sceneTags
      .getFeatureForEvent(event)
      .map(tag => tag.id);

    // Model attributes
    const modelData = await this.cadHandler.getProperties(event);

    const contextData = {
      selectedTags,
      modelData,
      sceneCoord
    } as ContextData;

    // Show default context menu
    showContextMenu(event, false, contextData);
  }

  setMovementState(state) {
    let orbit = this.orbit.controller;
    orbit.enableRotate = state;
    orbit.enablePan = state;
    orbit.enableZoom = state;
  }

  afterMouseUp() {
    if (this.mouseControls.movement && !this.orbit.state) {
      this.viewer.updatePickers();
    }

    this.mouseControls.upEnabled = false;
    this.mouseControls.movement = false;
  }

  getCameraDistance() {
    let minDistance = 1.0;
    let distance = minDistance;

    if (this.orbit.state) {
      let orbitObject = this.orbit.controller;
      let cameraPosition = orbitObject.object.position;
      let cameraPivot = orbitObject.target;
      let pivotLength = cameraPivot.distanceTo(cameraPosition);
      distance = Math.max(minDistance, pivotLength);
    }

    return distance;
  }

  getPrecision(precision) {
    let approxDistance = this.getCameraDistance();

    if (precision === null) {
      precision = Math.abs((1 - (100 - approxDistance) / 100.0) * (0.95) + 0.05);
      precision = Math.min(precision, 100.00);
      precision = Math.max(precision, 0.05);
    }

    let multiplier = this.viewer.far / this.viewer.defaultFarPlane;
    precision = precision * multiplier;

    return precision;
  }

  touchHandler(event) {
    let mouseEvent = touchToMouse(this, event);
    if (!mouseEvent) {
      return;
    }

    event.preventDefault();
    event.changedTouches[0].target.dispatchEvent(mouseEvent);
  }

  update(changeDetector: ChangeDetector) {
    this.cameraAlignerAdv.update(changeDetector);
    this.localAligner.update(changeDetector);
    this.pointMarkup.update();

    if (this.flyControls.enabled) {
      this.flyControls.move();
    }

    if (!this.enabled) return;

    this.mouseControls.update();
    this.orbit.update();
  }
}

class FlyMoveControls {
  public enabled;
  private panoControls: PanoControls;
  private numberOfSteps = 20;
  private positions = [];

  constructor(parent) {
    this.panoControls = parent;
    this.disable();
  }

  get viewer() {
    return this.panoControls.viewer;
  }

  get orbit() {
    return this.panoControls.orbit;
  }

  enable(positions) {
    this.enabled = true;
    this.positions = positions;
  }

  disable() {
    this.enabled = false;
    this.positions = [];
  }

  getAngleDiff(sphereStart, sphereFinish) {
    let dRadius = sphereFinish.radius - sphereStart.radius;
    let dPhi = sphereFinish.phi - sphereStart.phi;
    let dTheta = sphereFinish.theta - sphereStart.theta;
    if (dTheta > 180) {
      dTheta = -1 * (360 - dTheta);
    } else if (dTheta < -180) {
      dTheta = dTheta + 360;
    }

    return {dRadius, dTheta, dPhi};
  }

  start(pivot, orbit) {
    let currentOrbit = this.orbit.activePosition;
    let currentPivot = this.orbit.activeTarget;

    let sphereStart = new SphericalPosition()
      .fromVectors(currentPivot, currentOrbit);

    let sphereFinish = new SphericalPosition()
      .fromVectors(pivot, orbit);

    let {dRadius, dTheta, dPhi} = this.getAngleDiff(
      sphereStart, sphereFinish);

    let dPivot = new Vector3()
      .add(pivot)
      .sub(currentPivot);

    let positions = [];
    for (let i = 0; i <= this.numberOfSteps; i++) {
      // Sine function provide smoother movement
      let value = (Math.PI/2) * (i / this.numberOfSteps);
      value = Math.sin(value);
      value = Math.pow(value, 0.5);

      const pivotOffset = dPivot.clone()
        .multiplyScalar(value);

      const newPivot = new Vector3()
        .add(currentPivot)
        .add(pivotOffset);

      let theta = sphereStart.theta + dTheta * value;
      let radius = sphereStart.radius + dRadius * value;
      let phi = sphereStart.phi + dPhi * value;

      let newOrbit = new SphericalPosition()
        .fromValues(radius, theta, phi)
        .toVector3()
        .add(newPivot);

      positions.push({
        orbit: newOrbit,
        pivot: newPivot
      });
    }

    this.enable(positions);
  }

  move() {
    if (this.positions.length === 0) {
      this.disable();
      return;
    }

    let {orbit, pivot} = this.positions.shift();
    this.orbit.setPositionValues(orbit, pivot);
  }
}

class OrbitController {
  private viewer: Viewer;
  private controls: PanoControls;
  public controller;

  public rotateLength = 10.0;
  public movement = false;
  public camera;
  public callback;

  constructor(viewer: Viewer, controls: PanoControls, up) {
    this.viewer = viewer;
    this.controls = controls;

    this.camera = this.controls.object.clone();
    this.camera.isPerspectiveCamera = true;
    this.camera.up.copy(up);

    this.controller = new CustomOrbitControls(
      this.viewer, this.camera, this.controls.domElement);

    this.controller.addEventListener("stop", () => {
      this.viewer.updatePickers();
    });

    this.controller.enableDamping = true;
    this.controller.dampingFactor = 0.15;
    this.controller.rotateSpeed = 0.50;
    this.controller.panSpeed = 0.50;
    this.controller.enabled = false;
  }

  get state() {
    return this.controller.enabled;
  }

  get walkMode() {
    return this.controller.walkMode;
  }

  get activePosition() {
    return this.controller.activePosition();
  }

  get activeTarget() {
    return this.controller.activeTarget();
  }

  setControls(value) {
    this.controller.setControls(value);
  }

  setWalkMode(state) {
    this.controller.setWalkMode(state);
  }

  getPositionValues() {
    let pivot = null;
    let orbit = null;

    try {
      orbit = new SceneCoordinate(this.activePosition)
        .toDataProjection()
        .toArray();

      pivot = new SceneCoordinate(this.activeTarget)
        .toDataProjection()
        .toArray();
    } catch {
      // Nothing
    }

    return {orbit, pivot};
  }

  setPositionValues(orbit, pivot, enabled=true, smooth=false) {
    if (smooth) {
      this.controls.flyControls.start(pivot, orbit);
    } else {
      this.controller.position0.set(orbit.x, orbit.y, orbit.z);
      this.controller.target0.set(pivot.x, pivot.y, pivot.z);
      this.controller.reset();
    }

    this.controller.enabled = enabled;
  }

  object() {
    return this.controls;
  }

  toggle(value) {
    if (this.state) {
      this.controller.enabled = value;
    }
  }

  update() {
    if (!this.state) {
      return;
    }

    this.controller.update();
  }
}
